import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { Ridgeline } from "@/components/common/Ridgeline";
import {
  getCity,
  PROPERTY_TYPES,
  countBy,
  availableCountBy,
  listingsBy,
} from "@/data/rentals";

/**
 * /rentals/:city — property type selector for a given city.
 */
export default function RentalCity() {
  const { city: citySlug } = useParams();
  const city = getCity(citySlug);
  if (!city) return <Navigate to="/rentals" replace />;

  return (
    <div data-testid={`page-rental-city-${city.slug}`}>
      {/* ============ HEADER ============ */}
      <header
        className="relative overflow-hidden"
        style={{ backgroundColor: "#24382E" }}
        data-nav-theme="dark"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${city.hero})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.32,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(20,35,27,0.7) 0%, rgba(20,35,27,0.55) 60%, #24382E 100%)",
          }}
        />
        <div className="relative mx-auto max-w-[1440px] px-6 pb-16 pt-[128px] md:px-10 md:pb-24 md:pt-[168px]">
          <Reveal>
            <Link
              to="/rentals"
              className="group inline-flex items-center gap-2"
              style={{ color: "rgba(248,245,239,0.75)" }}
              data-testid="back-to-rentals"
            >
              <ArrowLeft size={16} />
              <span className="label-caps text-[11px]">All rentals</span>
            </Link>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 items-end gap-10 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-8">
              <Reveal delay={0.05}>
                <Eyebrow color="#C89A4E">{city.province}</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h1
                  className="font-display mt-6"
                  style={{
                    fontSize: "clamp(56px, 9vw, 132px)",
                    color: "#F8F5EF",
                    lineHeight: 0.9,
                    letterSpacing: "-0.02em",
                  }}
                  data-testid="rental-city-title"
                >
                  {city.name}
                </h1>
              </Reveal>
              <Reveal delay={0.14}>
                <p
                  className="body-measure mt-6 text-[17px] md:text-[19px]"
                  style={{ color: "rgba(237,233,224,0.8)" }}
                >
                  {city.intro}
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Reveal delay={0.2}>
                <p
                  className="font-display"
                  style={{
                    fontSize: "clamp(40px, 4.5vw, 64px)",
                    color: "#F8F5EF",
                    lineHeight: 0.9,
                  }}
                  data-testid="rental-city-count"
                >
                  {countBy({ city: city.slug })}
                </p>
                <p
                  className="label-caps mt-2 text-[11px]"
                  style={{ color: "rgba(237,233,224,0.6)" }}
                >
                  Homes in {city.name} · {availableCountBy({ city: city.slug })}{" "}
                  available
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </header>

      {/* ============ TYPE CARDS ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#F8F5EF" }}
        data-nav-theme="light"
        data-testid="rental-type-selector"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <Reveal>
            <Eyebrow>Pick your kind of place</Eyebrow>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
            {PROPERTY_TYPES.map((t, i) => {
              const total = countBy({ city: city.slug, type: t.slug });
              const available = availableCountBy({
                city: city.slug,
                type: t.slug,
              });
              const preview = listingsBy({
                city: city.slug,
                type: t.slug,
              }).slice(0, 3);
              return (
                <Reveal key={t.slug} delay={i * 0.06}>
                  <TypeCard
                    city={city}
                    type={t}
                    total={total}
                    available={available}
                    preview={preview}
                  />
                </Reveal>
              );
            })}
          </div>
        </div>
        <div className="w-full opacity-70" aria-hidden>
          <Ridgeline stroke="#C89A4E" height={90} />
        </div>
      </section>
    </div>
  );
}

const TypeCard = ({ city, type, total, available, preview }) => {
  const empty = total === 0;
  return (
    <Link
      to={empty ? "#" : `/rentals/${city.slug}/${type.slug}`}
      onClick={(e) => empty && e.preventDefault()}
      className={`group relative block overflow-hidden ${
        empty ? "cursor-not-allowed opacity-60" : ""
      }`}
      style={{
        borderRadius: 22,
        backgroundColor: "#EDE9E0",
        border: "1px solid rgba(32,30,26,0.08)",
      }}
      data-testid={`rental-type-${type.slug}`}
    >
      <div className="grid grid-cols-3 gap-1 p-3">
        {preview.length === 0 ? (
          <div
            className="col-span-3"
            style={{
              aspectRatio: "16 / 9",
              backgroundColor: "#E4DFD3",
              borderRadius: 14,
            }}
          />
        ) : (
          preview.map((p, i) => (
            <div
              key={p.slug}
              className="relative overflow-hidden"
              style={{
                aspectRatio: i === 0 ? "1 / 1" : "1 / 1",
                borderRadius: 14,
                gridColumn: i === 0 ? "span 2" : undefined,
                gridRow: i === 0 && preview.length > 1 ? "span 2" : undefined,
              }}
            >
              <img
                src={p.hero}
                alt={p.name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
            </div>
          ))
        )}
      </div>

      <div className="flex items-end justify-between gap-4 p-7 pt-4 md:p-9 md:pt-6">
        <div>
          <p
            className="label-caps text-[10px]"
            style={{ color: "#C89A4E" }}
          >
            {total} home{total === 1 ? "" : "s"} · {available} available
          </p>
          <h2
            className="font-display mt-2"
            style={{
              fontSize: "clamp(32px, 3.6vw, 48px)",
              color: "#201E1A",
              lineHeight: 1,
              letterSpacing: "-0.01em",
            }}
          >
            {type.name}
          </h2>
          <p
            className="mt-3 text-[15px]"
            style={{ color: "rgba(32,30,26,0.68)" }}
          >
            {type.tagline}
          </p>
        </div>
        <div className="mb-1 shrink-0">
          <ArrowUpRight
            size={22}
            color="#24382E"
            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </div>
      </div>
    </Link>
  );
};
